import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { toast } from "sonner";
import { useLogger } from "./use-logger";
import { initialAppState, type ServerWithName } from "@/state/app-types";
import { appReducer } from "@/state/app-reducer";
import { loadAppState, saveAppState } from "@/state/storage";
import {
  testConnection,
  deleteServer,
  listServers,
  reconnectServer,
  getInitializationInfo,
} from "@/state/mcp-api";
import {
  ensureAuthorizedForReconnect,
  type OAuthResult,
} from "@/state/oauth-orchestrator";
import type { ServerFormData } from "@/shared/types.js";
import { toMCPConfig } from "@/state/server-helpers";
import {
  handleOAuthCallback,
  getStoredTokens,
  clearOAuthData,
} from "@/lib/mcp-oauth";
import { MCPServerConfig } from "@/sdk";
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
export type { ServerWithName } from "@/state/app-types";

export function useAppState() {
  const logger = useLogger("Connections");

  const [appState, dispatch] = useReducer(appReducer, initialAppState);
  const [isLoading, setIsLoading] = useState(true);
  // Operation guard to avoid races
  const opTokenRef = useRef<Map<string, number>>(new Map());
  const nextOpToken = (name: string) => {
    const current = opTokenRef.current.get(name) ?? 0;
    const next = current + 1;
    opTokenRef.current.set(name, next);
    return next;
  };
  const isStaleOp = (name: string, token: number) =>
    (opTokenRef.current.get(name) ?? 0) !== token;

  // Convex integration for AppWorkspace servers
  const {
    activeWorkspaceId,
    isLoadingWorkspaces,
    workspaces,
    activeWorkspace,
  } = useAppWorkspace();
  const { isAuthenticated: isConvexAuthenticated } = useConvexAuth();

  // Track previous workspace ID to detect workspace switches
  const prevWorkspaceIdRef = useRef<string | null>(null);

  // Track auto-connect attempts to prevent infinite loops
  const autoConnectAttemptedRef = useRef<Set<string>>(new Set());

  // Track first load to trigger auto-connect
  const isFirstLoadRef = useRef<boolean>(true);

  // Store handleReconnect reference to use from setTimeout
  const handleReconnectRef = useRef<
    ((serverName: string) => Promise<{ success: true } | undefined>) | null
  >(null);

  // Verify the activeWorkspaceId is actually in the user's workspaces
  // This prevents querying with a stale workspace ID from localStorage
  const isValidWorkspaceId = activeWorkspaceId
    ? workspaces.some((w) => w._id === activeWorkspaceId)
    : false;

  // Query servers from Convex for active workspace
  // Skip if not authenticated, no workspace ID, workspaces are still loading,
  // or the workspace ID is stale/invalid
  const convexServers = useQuery(
    api.appWorkspaceServers.listServers,
    isConvexAuthenticated &&
      activeWorkspaceId &&
      !isLoadingWorkspaces &&
      isValidWorkspaceId
      ? { workspaceId: activeWorkspaceId }
      : "skip",
  );

  // Convex mutations for server management
  const addServerMutation = useMutation(api.appWorkspaceServers.addServer);
  const updateServerMutation = useMutation(
    api.appWorkspaceServers.updateServer,
  );
  const removeServerMutation = useMutation(
    api.appWorkspaceServers.removeServer,
  );

  // Load from storage once
  useEffect(() => {
    try {
      const loaded = loadAppState();
      dispatch({ type: "HYDRATE_STATE", payload: loaded });
    } catch (error) {
      logger.error("Failed to load saved state", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [logger]);

  // Persist on change
  useEffect(() => {
    if (!isLoading) saveAppState(appState);
  }, [appState, isLoading]);

  // Sync servers from Convex when they change
  useEffect(() => {
    if (
      !isConvexAuthenticated ||
      !activeWorkspaceId ||
      !convexServers ||
      isLoading ||
      isLoadingWorkspaces
    )
      return;

    // Detect workspace switch
    const workspaceSwitched =
      prevWorkspaceIdRef.current !== null &&
      prevWorkspaceIdRef.current !== activeWorkspaceId;

    if (workspaceSwitched) {
      logger.info("AppWorkspace switched, clearing all servers", {
        from: prevWorkspaceIdRef.current,
        to: activeWorkspaceId,
      });

      // Clear ALL servers when switching AppWorkspaces
      Object.keys(appState.servers).forEach((serverName) => {
        dispatch({
          type: "REMOVE_SERVER",
          name: serverName,
        });
      });

      // Clear auto-connect tracking for previous workspace to allow reconnection when switching back
      if (prevWorkspaceIdRef.current) {
        const keysToRemove = Array.from(autoConnectAttemptedRef.current).filter(
          (key) => key.startsWith(`${prevWorkspaceIdRef.current}:`),
        );
        keysToRemove.forEach((key) =>
          autoConnectAttemptedRef.current.delete(key),
        );
      }
    }

    // Update the workspace ref
    prevWorkspaceIdRef.current = activeWorkspaceId;

    // Merge Convex servers with local connection state
    const mergedServers: Record<string, ServerWithName> = {};

    // Add all Convex servers, preserving local connection status if it exists
    convexServers.forEach((convexServer) => {
      const localServer = appState.servers[convexServer.name];

      // Convert Convex config to MCPServerConfig
      let mcpConfig: MCPServerConfig;
      if (convexServer.config.type === "stdio") {
        mcpConfig = {
          command: convexServer.config.command!,
          args: convexServer.config.args,
          env: convexServer.config.env,
        };
      } else {
        mcpConfig = {
          url: new URL(convexServer.config.url!),
        };
      }

      mergedServers[convexServer.name] = {
        name: convexServer.name,
        config: mcpConfig,
        // Preserve local state if available (unless we just switched AppWorkspaces)
        connectionStatus:
          !workspaceSwitched && localServer?.connectionStatus
            ? localServer.connectionStatus
            : "disconnected",
        lastConnectionTime:
          !workspaceSwitched && localServer?.lastConnectionTime
            ? localServer.lastConnectionTime
            : new Date(),
        retryCount:
          !workspaceSwitched && localServer?.retryCount
            ? localServer.retryCount
            : 0,
        lastError: !workspaceSwitched ? localServer?.lastError : undefined,
        enabled: convexServer.enabled ?? false,
        oauthTokens: !workspaceSwitched ? localServer?.oauthTokens : undefined, // Clear OAuth tokens on AppWorkspace switch
        initializationInfo: !workspaceSwitched
          ? localServer?.initializationInfo
          : undefined,
      };
    });

    // Update state with merged servers
    Object.entries(mergedServers).forEach(([name, server]) => {
      dispatch({
        type: "UPSERT_SERVER",
        name,
        server,
      });
    });

    // Auto-connect to primary server after workspace switch or first load
    // Do this AFTER servers are merged into state to avoid "server not found" errors
    if (workspaceSwitched || isFirstLoadRef.current) {
      isFirstLoadRef.current = false;

      const currentWorkspace = workspaces.find(
        (w) => w._id === activeWorkspaceId,
      );
      if (currentWorkspace?.primaryServerId) {
        const primaryServer = convexServers.find(
          (s) => s._id === currentWorkspace.primaryServerId,
        );
        if (primaryServer) {
          const autoConnectKey = `${activeWorkspaceId}:${primaryServer.name}`;

          // Only auto-connect if we haven't already attempted for this workspace+server
          if (!autoConnectAttemptedRef.current.has(autoConnectKey)) {
            autoConnectAttemptedRef.current.add(autoConnectKey);

            // Use setTimeout to:
            // 1. Avoid blocking the sync effect
            // 2. Ensure handleReconnect is available (defined later in the hook)
            // 3. Allow React to finish state updates
            setTimeout(() => {
              logger.info("Auto-connecting to primary server", {
                serverName: primaryServer.name,
                workspaceId: activeWorkspaceId,
              });

              // Use handleReconnectRef to avoid closure issues
              if (handleReconnectRef.current) {
                handleReconnectRef.current(primaryServer.name).catch((err) => {
                  logger.error("Failed to auto-reconnect primary server", {
                    serverName: primaryServer.name,
                    error: err instanceof Error ? err.message : String(err),
                  });
                  // Remove from attempted set on error to allow manual retry
                  autoConnectAttemptedRef.current.delete(autoConnectKey);
                });
              }
            }, 100);
          }
        }
      }
    }

    // Remove servers that are no longer in Convex (only if we didn't just switch workspaces)
    if (!workspaceSwitched) {
      const convexServerNames = new Set(convexServers.map((s) => s.name));
      Object.keys(appState.servers).forEach((localServerName) => {
        if (!convexServerNames.has(localServerName)) {
          // Only remove if it's not connected (to avoid disrupting active connections)
          const localServer = appState.servers[localServerName];
          if (
            localServer.connectionStatus === "disconnected" ||
            localServer.connectionStatus === "failed"
          ) {
            dispatch({
              type: "REMOVE_SERVER",
              name: localServerName,
            });
          }
        }
      });
    }
  }, [
    isConvexAuthenticated,
    activeWorkspaceId,
    convexServers,
    isLoading,
    isLoadingWorkspaces,
    logger,
    workspaces,
  ]);

  const validateForm = (formData: ServerFormData): string | null => {
    if (formData.type === "stdio") {
      if (!formData.command || formData.command.trim() === "") {
        return "Command is required for STDIO connections";
      }
      return null;
    }
    if (!formData.url || formData.url.trim() === "") {
      return "URL is required for HTTP connections";
    }
    try {
      new URL(formData.url);
    } catch (err) {
      return `Invalid URL format: ${formData.url} ${err}`;
    }
    return null;
  };

  const setSelectedMultipleServersToAllServers = useCallback(() => {
    const connectedNames = Object.entries(appState.servers)
      .filter(([, s]) => s.connectionStatus === "connected")
      .map(([name]) => name);
    dispatch({ type: "SET_MULTI_SELECTED", names: connectedNames });
  }, [appState.servers]);

  // OAuth callback finish handler
  const handleOAuthCallbackComplete = useCallback(
    async (code: string) => {
      window.history.replaceState({}, document.title, window.location.pathname);
      try {
        const result = await handleOAuthCallback(code);
        if (result.success && result.serverConfig && result.serverName) {
          const serverName = result.serverName;

          // Move to connecting with fresh OAuth config
          dispatch({
            type: "CONNECT_REQUEST",
            name: serverName,
            config: result.serverConfig,
            select: true,
          });

          // Test the connection - use short timeout for fast retries
          try {
            const connectionConfig = { ...result.serverConfig, timeout: 5000 };
            const connectionResult = await testConnection(
              connectionConfig,
              serverName,
            );
            if (connectionResult.success) {
              dispatch({
                type: "CONNECT_SUCCESS",
                name: serverName,
                config: result.serverConfig,
                tokens: getStoredTokens(serverName),
              });

              // Save to Convex if workspace is active and authentication is ready
              if (
                isConvexAuthenticated &&
                activeWorkspaceId &&
                !isLoadingWorkspaces
              ) {
                try {
                  const cfg = result.serverConfig as any;
                  await addServerMutation({
                    workspaceId: activeWorkspaceId,
                    name: serverName,
                    config: {
                      type: cfg.type || "http",
                      command: cfg.command,
                      args: cfg.args,
                      url: cfg.url?.toString(),
                      env: cfg.env,
                    },
                    enabled: true,
                  });
                } catch (convexError) {
                  logger.error("Failed to save OAuth server to Convex", {
                    serverName,
                    error:
                      convexError instanceof Error
                        ? convexError.message
                        : "Unknown error",
                  });
                }
              }

              // Fetch initialization info after successful connection
              await fetchAndStoreInitInfo(serverName);
              logger.info("OAuth connection successful", { serverName });
              toast.success(
                `OAuth connection successful! Connected to ${serverName}.`,
              );
            } else {
              dispatch({
                type: "CONNECT_FAILURE",
                name: serverName,
                error:
                  connectionResult.error ||
                  "Connection test failed after OAuth",
              });
              logger.error("OAuth connection test failed", {
                serverName,
                error: connectionResult.error,
              });
              toast.error(
                `OAuth succeeded but connection test failed: ${connectionResult.error}`,
              );
            }
          } catch (connectionError) {
            const errorMessage =
              connectionError instanceof Error
                ? connectionError.message
                : "Unknown connection error";
            dispatch({
              type: "CONNECT_FAILURE",
              name: serverName,
              error: errorMessage,
            });
            logger.error("OAuth connection test error", {
              serverName,
              error: errorMessage,
            });
            toast.error(
              `OAuth succeeded but connection test failed: ${errorMessage}`,
            );
          }
        } else {
          throw new Error(result.error || "OAuth callback failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast.error(`Error completing OAuth flow: ${errorMessage}`);
        logger.error("OAuth callback failed", { error: errorMessage });
      }
    },
    [logger],
  );

  // Check for OAuth callback completion on mount
  useEffect(() => {
    // Skip OAuth callback handling if we're on the debug callback page
    // The debug callback page handles its own OAuth flow visualization
    if (window.location.pathname.startsWith("/oauth/callback/debug")) {
      return;
    }

    if (!isLoading) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const error = urlParams.get("error");

      // Only handle as MCP OAuth if there's a pending MCP OAuth flow
      // Otherwise, let WorkOS AuthKit handle the callback
      const hasPendingMCPOAuth = localStorage.getItem("mcp-oauth-pending");

      if (code && hasPendingMCPOAuth) {
        handleOAuthCallbackComplete(code);
      } else if (error && hasPendingMCPOAuth) {
        toast.error(`OAuth authorization failed: ${error}`);
        localStorage.removeItem("mcp-oauth-pending");
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
      // If no pending MCP OAuth, let WorkOS AuthKit handle it (no action needed)
    }
  }, [isLoading, handleOAuthCallbackComplete]);

  // Helper to fetch and store initialization info
  const fetchAndStoreInitInfo = useCallback(async (serverName: string) => {
    try {
      const result = await getInitializationInfo(serverName);
      if (result.success && result.initInfo) {
        dispatch({
          type: "SET_INITIALIZATION_INFO",
          name: serverName,
          initInfo: result.initInfo,
        });
      }
    } catch (error) {
      // Silent fail - initialization info is optional
      console.debug("Failed to fetch initialization info", {
        serverName,
        error,
      });
    }
  }, []);

  const handleConnect = useCallback(
    async (formData: ServerFormData) => {
      const validationError = validateForm(formData);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const mcpConfig = toMCPConfig(formData);
      dispatch({
        type: "CONNECT_REQUEST",
        name: formData.name,
        config: mcpConfig,
        select: true,
      });
      const token = nextOpToken(formData.name);

      try {
        if (formData.type === "http" && formData.useOAuth && formData.url) {
          // Mark oauth-flow status for UI while initiating
          dispatch({
            type: "UPSERT_SERVER",
            name: formData.name,
            server: {
              name: formData.name,
              config: mcpConfig,
              lastConnectionTime: new Date(),
              connectionStatus: "oauth-flow",
              retryCount: 0,
              enabled: true,
            } as ServerWithName,
          });

          const { initiateOAuth } = await import("@/lib/mcp-oauth");
          const oauthOptions: any = {
            serverName: formData.name,
            serverUrl: formData.url,
            clientId: formData.clientId,
            clientSecret: formData.clientSecret,
          };
          if (formData.oauthScopes && formData.oauthScopes.length > 0) {
            oauthOptions.scopes = formData.oauthScopes;
          }
          const oauthResult = await initiateOAuth(oauthOptions);
          if (oauthResult.success) {
            if (oauthResult.serverConfig) {
              // Save to Convex FIRST (before testing connection)
              // This ensures the server config is persisted regardless of connection success
              if (
                isConvexAuthenticated &&
                activeWorkspaceId &&
                !isLoadingWorkspaces
              ) {
                try {
                  const cfg = oauthResult.serverConfig as any;
                  await addServerMutation({
                    workspaceId: activeWorkspaceId,
                    name: formData.name,
                    config: {
                      type: cfg.type || formData.type,
                      command: cfg.command || formData.command,
                      args: cfg.args || formData.args,
                      url: cfg.url?.toString() || formData.url,
                      env: cfg.env || formData.env,
                    },
                    enabled: true,
                  });
                  logger.info("OAuth server config saved to Convex", {
                    serverName: formData.name,
                  });
                } catch (convexError) {
                  logger.error("Failed to save OAuth server to Convex", {
                    serverName: formData.name,
                    error:
                      convexError instanceof Error
                        ? convexError.message
                        : "Unknown error",
                  });
                }
              }

              // Use short timeout for connection attempts to enable fast retries
              const connectionConfig = {
                ...oauthResult.serverConfig,
                timeout: 5000,
              };
              const connectionResult = await testConnection(
                connectionConfig,
                formData.name,
              );
              if (isStaleOp(formData.name, token)) return;
              if (connectionResult.success) {
                dispatch({
                  type: "CONNECT_SUCCESS",
                  name: formData.name,
                  config: oauthResult.serverConfig,
                  tokens: getStoredTokens(formData.name),
                });

                // Fetch initialization info after successful connection
                await fetchAndStoreInitInfo(formData.name);
                toast.success(`Connected successfully with OAuth!`);
              } else {
                dispatch({
                  type: "CONNECT_FAILURE",
                  name: formData.name,
                  error:
                    connectionResult.error || "OAuth connection test failed",
                });
                toast.error(
                  `OAuth succeeded but connection failed: ${connectionResult.error}`,
                );
              }
            } else {
              toast.success(
                "OAuth flow initiated. You will be redirected to authorize access.",
              );
            }
            return;
          } else {
            if (isStaleOp(formData.name, token)) return;
            dispatch({
              type: "CONNECT_FAILURE",
              name: formData.name,
              error: oauthResult.error || "OAuth initialization failed",
            });
            toast.error(`OAuth initialization failed: ${oauthResult.error}`);
            return;
          }
        }

        // Save to Convex FIRST (before testing connection)
        // This ensures the server config is persisted regardless of connection success
        if (
          isConvexAuthenticated &&
          activeWorkspaceId &&
          !isLoadingWorkspaces
        ) {
          try {
            await addServerMutation({
              workspaceId: activeWorkspaceId,
              name: formData.name,
              config: {
                type: formData.type,
                command: formData.command,
                args: formData.args,
                url: formData.url,
                env: formData.env,
              },
              enabled: true,
            });
            logger.info("Server config saved to Convex", {
              serverName: formData.name,
            });
          } catch (convexError) {
            // Log but don't fail - we can still try to connect locally
            logger.error("Failed to save server to Convex", {
              serverName: formData.name,
              error:
                convexError instanceof Error
                  ? convexError.message
                  : "Unknown error",
            });
          }
        }

        // Non-OAuth connect - use short timeout for fast retries
        const connectionConfig = { ...mcpConfig, timeout: 5000 };
        const result = await testConnection(connectionConfig, formData.name);
        if (isStaleOp(formData.name, token)) return;
        if (result.success) {
          dispatch({
            type: "CONNECT_SUCCESS",
            name: formData.name,
            config: mcpConfig,
          });

          // Fetch initialization info after successful connection
          await fetchAndStoreInitInfo(formData.name);
          logger.info("Connection successful", { serverName: formData.name });
          toast.success(`Connected successfully!`);
        } else {
          dispatch({
            type: "CONNECT_FAILURE",
            name: formData.name,
            error: result.error || "Connection test failed",
          });
          logger.error("Connection failed", {
            serverName: formData.name,
            error: result.error,
          });
          toast.error(`Failed to connect to ${formData.name}`);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        if (isStaleOp(formData.name, token)) return;
        dispatch({
          type: "CONNECT_FAILURE",
          name: formData.name,
          error: errorMessage,
        });
        logger.error("Connection failed", {
          serverName: formData.name,
          error: errorMessage,
        });
        toast.error(`Network error: ${errorMessage}`);
      }
    },
    [
      appState.servers,
      logger,
      fetchAndStoreInitInfo,
      isConvexAuthenticated,
      activeWorkspaceId,
      isLoadingWorkspaces,
      addServerMutation,
    ],
  );

  // CLI config processing guard
  const cliConfigProcessedRef = useRef<boolean>(false);

  // Auto-connect to CLI-provided MCP server(s) on mount
  useEffect(() => {
    if (!isLoading && !cliConfigProcessedRef.current) {
      cliConfigProcessedRef.current = true;
      // Fetch CLI config from API (both dev and production)
      fetch("/api/mcp-cli-config")
        .then((response) => response.json())
        .then((data) => {
          const cliConfig = data.config;
          if (cliConfig) {
            // Handle multiple servers from config file
            if (cliConfig.servers && Array.isArray(cliConfig.servers)) {
              const autoConnectServer = cliConfig.autoConnectServer;

              logger.info(
                "Processing CLI-provided MCP servers (from config file)",
                {
                  serverCount: cliConfig.servers.length,
                  autoConnectServer: autoConnectServer || "all",
                  cliConfig: cliConfig,
                },
              );

              // Add all servers to the UI, but only auto-connect to filtered ones
              cliConfig.servers.forEach((server: any) => {
                const formData: ServerFormData = {
                  name: server.name || "CLI Server",
                  type: (server.type === "sse"
                    ? "http"
                    : server.type || "stdio") as "stdio" | "http",
                  command: server.command,
                  args: server.args || [],
                  url: server.url,
                  env: server.env || {},
                };

                // Always add/update server from CLI config
                const mcpConfig = toMCPConfig(formData);
                dispatch({
                  type: "UPSERT_SERVER",
                  name: formData.name,
                  server: {
                    name: formData.name,
                    config: mcpConfig,
                    lastConnectionTime: new Date(),
                    connectionStatus: "disconnected" as const,
                    retryCount: 0,
                    enabled: false, // Start disabled, will enable on successful connection
                  },
                });

                // Only auto-connect if matches filter (or no filter)
                if (!autoConnectServer || server.name === autoConnectServer) {
                  logger.info("Auto-connecting to server", {
                    serverName: server.name,
                  });
                  handleConnect(formData);
                } else {
                  logger.info("Skipping auto-connect for server", {
                    serverName: server.name,
                    reason: "filtered out",
                  });
                }
              });
              return;
            }
            // Handle legacy single server mode
            if (cliConfig.command) {
              logger.info("Auto-connecting to CLI-provided MCP server", {
                cliConfig,
              });
              const formData: ServerFormData = {
                name: cliConfig.name || "CLI Server",
                type: "stdio" as const,
                command: cliConfig.command,
                args: cliConfig.args || [],
                env: cliConfig.env || {},
              };
              handleConnect(formData);
            }
          }
        })
        .catch((error) => {
          logger.debug("Could not fetch CLI config from API", { error });
        });
    }
  }, [isLoading, handleConnect, logger, appState.servers]);

  const getValidAccessToken = useCallback(
    async (serverName: string): Promise<string | null> => {
      const server = appState.servers[serverName];
      if (!server?.oauthTokens) return null;
      return server.oauthTokens.access_token || null;
    },
    [appState.servers],
  );

  const handleDisconnect = useCallback(async (serverName: string) => {
    logger.info("Disconnecting from server", { serverName });
    dispatch({ type: "DISCONNECT", name: serverName });
    try {
      const result = await deleteServer(serverName);
      if (!result.success) {
        dispatch({ type: "DISCONNECT", name: serverName, error: result.error });
      }
    } catch (error) {
      dispatch({
        type: "DISCONNECT",
        name: serverName,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }, []);

  const handleRemoveServer = useCallback(
    async (serverName: string) => {
      logger.info("Removing server", { serverName });
      clearOAuthData(serverName);
      dispatch({ type: "REMOVE_SERVER", name: serverName });

      // Remove from Convex if workspace is active, authentication is ready, and server exists in Convex
      if (
        isConvexAuthenticated &&
        activeWorkspaceId &&
        !isLoadingWorkspaces &&
        convexServers
      ) {
        const serverExistsInConvex = convexServers.some(
          (s) => s.name === serverName,
        );
        if (serverExistsInConvex) {
          try {
            await removeServerMutation({
              workspaceId: activeWorkspaceId,
              name: serverName,
            });
            logger.info("Server removed from Convex", { serverName });
          } catch (convexError) {
            logger.error("Failed to remove server from Convex", {
              serverName,
              error:
                convexError instanceof Error
                  ? convexError.message
                  : "Unknown error",
            });
            // Don't show error to user - local removal succeeded
          }
        } else {
          logger.info("Server not in Convex, skipping Convex removal", {
            serverName,
          });
        }
      }
    },
    [
      isConvexAuthenticated,
      activeWorkspaceId,
      isLoadingWorkspaces,
      convexServers,
      removeServerMutation,
      logger,
    ],
  );

  const handleReconnect = useCallback(
    async (serverName: string, maxRetries = 3) => {
      logger.info("Reconnecting to server", { serverName });
      const server = appState.servers[serverName];
      if (!server) throw new Error(`Server ${serverName} not found`);

      let lastError: string | undefined;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        dispatch({ type: "RECONNECT_REQUEST", name: serverName });
        const token = nextOpToken(serverName);

        try {
          const authResult: OAuthResult =
            await ensureAuthorizedForReconnect(server);
          if (authResult.kind === "redirect") return; // UI shows oauth-flow during redirect
          if (authResult.kind === "error") {
            if (isStaleOp(serverName, token)) return;
            lastError = authResult.error;
            throw new Error(authResult.error);
          }

          // Use short timeout for connection attempts to enable fast retries
          const connectionConfig = {
            ...authResult.serverConfig,
            timeout: 5000, // 5 seconds for connection attempts
          };

          const result = await reconnectServer(serverName, connectionConfig);
          if (isStaleOp(serverName, token)) return;

          if (result.success) {
            dispatch({
              type: "CONNECT_SUCCESS",
              name: serverName,
              config: authResult.serverConfig,
              tokens: authResult.tokens,
            });

            // Update enabled status in Convex if workspace is active and authentication is ready
            if (
              isConvexAuthenticated &&
              activeWorkspaceId &&
              !isLoadingWorkspaces
            ) {
              try {
                await updateServerMutation({
                  workspaceId: activeWorkspaceId,
                  name: serverName,
                  enabled: true,
                });
              } catch (convexError) {
                logger.error(
                  "Failed to update server status in Convex on reconnect",
                  {
                    serverName,
                    error:
                      convexError instanceof Error
                        ? convexError.message
                        : "Unknown error",
                  },
                );
              }
            }

            // Fetch initialization info after successful reconnection
            await fetchAndStoreInitInfo(serverName);
            logger.info("Reconnection successful", {
              serverName,
              attempt,
              maxRetries,
            });
            return { success: true } as const;
          } else {
            lastError = result.error || "Reconnection failed";
            throw new Error(lastError);
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          lastError = errorMessage;

          if (isStaleOp(serverName, token)) return;

          // If not last attempt, wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 4000); // 1s, 2s, 4s max
            logger.warn(
              `Reconnection attempt ${attempt}/${maxRetries} failed, retrying in ${delay}ms`,
              {
                serverName,
                error: errorMessage,
              },
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            // Final attempt failed
            dispatch({
              type: "CONNECT_FAILURE",
              name: serverName,
              error: lastError,
            });
            logger.error("Reconnection failed after all attempts", {
              serverName,
              attempts: maxRetries,
              error: lastError,
            });
            toast.error(
              `Failed to connect to ${serverName} after ${maxRetries} attempts`,
            );
            throw error;
          }
        }
      }
    },
    [
      appState.servers,
      fetchAndStoreInitInfo,
      isConvexAuthenticated,
      activeWorkspaceId,
      isLoadingWorkspaces,
      updateServerMutation,
      logger,
    ],
  );

  // Store handleReconnect in ref so it can be used from setTimeout in workspace sync effect
  useEffect(() => {
    handleReconnectRef.current = handleReconnect;
  }, [handleReconnect]);

  // Sync with centralized agent status on app startup only
  useEffect(() => {
    if (isLoading) return;
    const syncServerStatus = async () => {
      try {
        const result = await listServers();
        if (result?.success && result.servers) {
          dispatch({ type: "SYNC_AGENT_STATUS", servers: result.servers });
        }
      } catch (error) {
        logger.debug("Failed to sync server status on startup", {
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    };
    syncServerStatus();
  }, [isLoading, logger]);

  const setSelectedServer = useCallback((serverName: string) => {
    dispatch({ type: "SELECT_SERVER", name: serverName });
  }, []);

  const setSelectedMCPConfigs = useCallback((serverNames: string[]) => {
    dispatch({ type: "SET_MULTI_SELECTED", names: serverNames });
  }, []);

  const toggleMultiSelectMode = useCallback((enabled: boolean) => {
    dispatch({ type: "SET_MULTI_MODE", enabled });
  }, []);

  const toggleServerSelection = useCallback(
    (serverName: string) => {
      const current = appState.selectedMultipleServers;
      const next = current.includes(serverName)
        ? current.filter((n) => n !== serverName)
        : [...current, serverName];
      dispatch({ type: "SET_MULTI_SELECTED", names: next });
    },
    [appState.selectedMultipleServers],
  );

  const handleUpdate = useCallback(
    async (
      originalServerName: string,
      formData: ServerFormData,
      skipAutoConnect?: boolean,
    ) => {
      const originalServer = appState.servers[originalServerName];

      // If skipAutoConnect is true, just update the config without reconnecting
      if (skipAutoConnect) {
        const mcpConfig = toMCPConfig(formData);
        dispatch({
          type: "CONNECT_SUCCESS",
          name: originalServerName,
          config: mcpConfig,
        });

        // Update in Convex if workspace is active and authentication is ready
        if (
          isConvexAuthenticated &&
          activeWorkspaceId &&
          !isLoadingWorkspaces
        ) {
          try {
            await updateServerMutation({
              workspaceId: activeWorkspaceId,
              name: originalServerName,
              config: {
                type: formData.type,
                command: formData.command,
                args: formData.args,
                url: formData.url,
                env: formData.env,
              },
              newName:
                formData.name !== originalServerName
                  ? formData.name
                  : undefined,
            });
          } catch (convexError) {
            logger.error("Failed to update server in Convex", {
              serverName: originalServerName,
              error:
                convexError instanceof Error
                  ? convexError.message
                  : "Unknown error",
            });
          }
        }

        toast.success("Server configuration updated");
        return;
      }

      const hadOAuthTokens = originalServer?.oauthTokens != null;
      const shouldPreserveOAuth =
        hadOAuthTokens &&
        formData.useOAuth &&
        formData.name === originalServerName &&
        formData.type === "http" &&
        formData.url === (originalServer?.config as any).url?.toString();

      if (shouldPreserveOAuth && originalServer) {
        const mcpConfig = toMCPConfig(formData);
        dispatch({
          type: "CONNECT_REQUEST",
          name: originalServerName,
          config: mcpConfig,
        });
        try {
          // Use short timeout for connection attempts
          const connectionConfig = { ...originalServer.config, timeout: 5000 };
          const result = await testConnection(
            connectionConfig,
            originalServerName,
          );
          if (result.success) {
            dispatch({
              type: "CONNECT_SUCCESS",
              name: originalServerName,
              config: mcpConfig,
            });
            // Fetch initialization info after successful update
            await fetchAndStoreInitInfo(originalServerName);
            toast.success("Server configuration updated successfully!");
            return;
          } else {
            console.warn(
              "OAuth connection test failed, falling back to full reconnect",
            );
          }
        } catch (error) {
          console.warn(
            "OAuth connection test error, falling back to full reconnect",
            error,
          );
        }
      }

      // Clear OAuth tokens if switching from OAuth to no authentication
      if (hadOAuthTokens && !formData.useOAuth) {
        clearOAuthData(originalServerName);
      }

      await handleDisconnect(originalServerName);

      // If server name changed, remove old server from Convex
      if (
        formData.name !== originalServerName &&
        isConvexAuthenticated &&
        activeWorkspaceId &&
        !isLoadingWorkspaces
      ) {
        try {
          await removeServerMutation({
            workspaceId: activeWorkspaceId,
            name: originalServerName,
          });
        } catch (convexError) {
          logger.error(
            "Failed to remove old server from Convex during rename",
            {
              serverName: originalServerName,
              error:
                convexError instanceof Error
                  ? convexError.message
                  : "Unknown error",
            },
          );
        }
      }

      await handleConnect(formData);
      if (
        appState.selectedServer === originalServerName &&
        formData.name !== originalServerName
      ) {
        setSelectedServer(formData.name);
      }
    },
    [
      appState.servers,
      appState.selectedServer,
      isConvexAuthenticated,
      activeWorkspaceId,
      isLoadingWorkspaces,
      handleDisconnect,
      handleConnect,
      removeServerMutation,
      updateServerMutation,
      logger,
      setSelectedServer,
    ],
  );

  return {
    // State
    appState,
    isLoading,

    // Computed values
    connectedServerConfigs: appState.servers,
    selectedServerEntry: appState.servers[appState.selectedServer],
    selectedMCPConfig: appState.servers[appState.selectedServer]?.config,
    selectedMCPConfigs: appState.selectedMultipleServers
      .map((name) => appState.servers[name])
      .filter(Boolean),
    selectedMCPConfigsMap: appState.selectedMultipleServers.reduce(
      (acc, name) => {
        if (appState.servers[name]) {
          acc[name] = appState.servers[name].config;
        }
        return acc;
      },
      {} as Record<string, MCPServerConfig>,
    ),
    isMultiSelectMode: appState.isMultiSelectMode,

    // Actions
    handleConnect,
    handleDisconnect,
    handleReconnect,
    handleUpdate,
    handleRemoveServer,
    setSelectedServer,
    setSelectedMCPConfigs,
    toggleMultiSelectMode,
    toggleServerSelection,
    getValidAccessToken,
    setSelectedMultipleServersToAllServers,
  };
}
